import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import MainLayout from './commodity/components/layout/MainLayout';
import PortalShell from './commodity/layouts/PortalShell';
import { TEACHER_ENVIRONMENT } from './design-system/teacher-environment';
import ConsoleShell from './commodity/layouts/ConsoleShell';
import { FAMILY_ENVIRONMENT } from './design-system/family-environment';
import { ADMIN_ENVIRONMENT } from './design-system/admin-environment';
import KidShell from './commodity/layouts/KidShell';
import { KID_ENVIRONMENT } from './design-system/kid-environment';
import { DesignedNotBuilt } from './design-system/portal';
import PrototypeScreen from './verticals/iqmax/screens/PrototypeScreen';
import TeacherReviews from './verticals/iqmax/screens/TeacherReviews';
import PublicLayout from './commodity/components/layout/PublicLayout';
import HomePage from './verticals/iqmax/screens/HomePage';
import TeacherLanding from './verticals/iqmax/screens/TeacherLanding';
import AuthChoicePage from './core/identity-access/screens/AuthChoicePage';
import Register from './core/identity-access/screens/Register';
import Login from './core/identity-access/screens/Login';
import VerifyEmail from './core/identity-access/screens/VerifyEmail';
import ForgotPassword from './core/identity-access/screens/ForgotPassword';
import ResetPassword from './core/identity-access/screens/ResetPassword';
import {
  getStoredTokens,
  saveTokens,
  clearTokens,
  isTokenExpired,
  decodeIdToken,
} from './adapters/api-client/tokenStorage';
import { onAuthExpired } from './adapters/api-client/api';
import { useSignOut } from './core/identity-access/store/signOut';
import { login, logout, setInitialized } from './core/identity-access/store/authSlice';
import { restoreContext } from './core/identity-access/store/contextSlice';
import { AppDispatch, RootState } from './store/store';
import ProtectedRoute from './core/identity-access/components/ProtectedRoute';
import KidModeGate from './core/identity-access/components/KidModeGate';
import KidModeSetup from './core/identity-access/screens/KidModeSetup';
import MyClasses from './verticals/iqmax/screens/learner/MyClasses';
import LearnerCalendar from './verticals/iqmax/screens/learner/LearnerCalendar';
import JoinClass from './verticals/iqmax/screens/learner/JoinClass';
import MyProgress from './verticals/iqmax/screens/learner/MyProgress';
import LearnerPastClasses from './verticals/iqmax/screens/learner/PastClasses';
import LearnerCourseOverview from './verticals/iqmax/screens/learner/LearnerCourseOverview';
import LearnerNotices from './verticals/iqmax/screens/learner/LearnerNotices';
import LearnerExplore from './verticals/iqmax/screens/learner/LearnerExplore';
import LearnerExploreCourse from './verticals/iqmax/screens/learner/LearnerExploreCourse';
import LearnerTeacher from './verticals/iqmax/screens/learner/LearnerTeacher';
import LearnerRequests from './verticals/iqmax/screens/learner/LearnerRequests';
import LearnerProfile from './verticals/iqmax/screens/learner/LearnerProfile';
import ConsentAcceptance from './core/identity-access/screens/ConsentAcceptance';
import CourseSearch from './verticals/iqmax/screens/CourseSearch';
import CourseDetail from './verticals/iqmax/screens/CourseDetail';
import EnrollmentConfirm from './verticals/iqmax/screens/EnrollmentConfirm';
import EnrollmentCheckoutReturn from './verticals/iqmax/screens/EnrollmentCheckoutReturn';
import SessionRoom from './verticals/iqmax/screens/SessionRoom';
import EnrollmentDetail from './verticals/iqmax/screens/EnrollmentDetail';
import SessionEvidence from './verticals/iqmax/screens/SessionEvidence';
import ParentProgress from './verticals/iqmax/screens/ParentProgress';
import TeacherProfile from './verticals/iqmax/screens/TeacherProfile';
import TeacherCourseDetail from './verticals/iqmax/screens/TeacherCourseDetail';
import TeacherOnboarding from './verticals/iqmax/screens/TeacherOnboarding';
import AdminTeacherApplications from './verticals/iqmax/screens/AdminTeacherApplications';
import AdminCourseReviews from './verticals/iqmax/screens/AdminCourseReviews';
import ParentDashboard from './verticals/iqmax/screens/ParentDashboard';
import PastClasses from './verticals/iqmax/screens/PastClasses';
import ParentLearning from './verticals/iqmax/screens/ParentLearning';
import ParentSchedule from './verticals/iqmax/screens/ParentSchedule';
import TeacherEvidenceRequests from './verticals/iqmax/screens/TeacherEvidenceRequests';
import AddStudent from './verticals/iqmax/screens/AddStudent';
import TeacherEarnings from './verticals/iqmax/screens/TeacherEarnings';
import TeacherSettlement from './verticals/iqmax/screens/TeacherSettlement';
import TeacherAvailability from './verticals/iqmax/screens/TeacherAvailability';
import TeacherCalendar from './verticals/iqmax/screens/TeacherCalendar';
import AdminDisputes from './verticals/iqmax/screens/AdminDisputes';
import AdminPayoutStatus from './verticals/iqmax/screens/AdminPayoutStatus';
import AdminSupportQueue from './verticals/iqmax/screens/AdminSupportQueue';
import AdminSupportCase from './verticals/iqmax/screens/AdminSupportCase';
import AdminProductHealthDashboard from './verticals/iqmax/screens/AdminProductHealthDashboard';
import AdminEarningsDashboard from './verticals/iqmax/screens/AdminEarningsDashboard';
import AdminOpsDashboard from './verticals/iqmax/screens/AdminOpsDashboard';
import AdminCaseQueue from './verticals/iqmax/screens/AdminCaseQueue';
import AdminAuditLog from './verticals/iqmax/screens/AdminAuditLog';
import AdminPlaybooks from './verticals/iqmax/screens/AdminPlaybooks';
import AdminParameters from './verticals/iqmax/screens/AdminParameters';
import AdminTrustChain from './verticals/iqmax/screens/AdminTrustChain';
import AdminTeacherProfile from './verticals/iqmax/screens/AdminTeacherProfile';
import AdminCoverage from './verticals/iqmax/screens/AdminCoverage';
import AdminGoNoGoDashboard from './verticals/iqmax/screens/AdminGoNoGoDashboard';
import AdminIntegrity from './verticals/iqmax/screens/AdminIntegrity';
import AdminSafetyInbox from './verticals/iqmax/screens/AdminSafetyInbox';
import AdminSafetyOperations from './verticals/iqmax/screens/AdminSafetyOperations';
import TeacherDashboard from './verticals/iqmax/screens/TeacherDashboard';
import TeacherCourses from './verticals/iqmax/screens/TeacherCourses';
import TeacherCourseForm from './verticals/iqmax/screens/TeacherCourseForm';
import Profile from './core/identity-access/screens/Profile';
import Settings from './core/identity-access/screens/Settings';
import { features } from './commodity/config/features';
import { homePathForRole } from './commodity/config/routes';
import Terms from './commodity/screens/Terms';
import Privacy from './commodity/screens/Privacy';
import Help from './commodity/screens/Help';
import Contact from './commodity/screens/Contact';

// ── Menu items ────────────────────────────────────────────────────────────────

const icon = (d: string) => (
  <svg viewBox="0 0 24 24" fill="none">
    <path
      d={d}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ICONS = {
  home: icon('M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z M9 22V12h6v10'),
  catalog: icon('M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z'),
  grid: icon('M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z'),
  plus: icon('M12 5v14M5 12h14'),
  calendar: icon('M3 5h18v16H3z M16 3v4M8 3v4M3 11h18'),
  money: icon('M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6'),
  user: icon('M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2 M12 3a4 4 0 110 8 4 4 0 010-8z'),
  gear: icon(
    'M12 15a3 3 0 100-6 3 3 0 000 6z M19.4 15a1.7 1.7 0 00.3 1.9l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-2.9 1.2v.2a2 2 0 11-4 0v-.1a1.7 1.7 0 00-3-1.2l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00-1.2-2.9H3a2 2 0 110-4h.1A1.7 1.7 0 004.3 6l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 002.9-1.2V2a2 2 0 114 0v.1a1.7 1.7 0 002.9 1.2l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.9z',
  ),
  chat: icon('M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z'),
};

interface MenuItem {
  path: string;
  label: string;
  icon: React.ReactElement;
  badge?: number;
}

const PROFILE_ITEMS: MenuItem[] = [
  { path: '/profile', label: 'Mi perfil', icon: ICONS.user },
  { path: '/settings', label: 'Configuración', icon: ICONS.gear },
];

/**
 * Navigation is built per role rather than by splicing a shared array (S10.1).
 *
 * Two rules this encodes, both from QA findings: a teacher's primary
 * destinations are their own console — not the public catalog, which offers to
 * enrol them in other teachers' courses — and deferred features are omitted
 * entirely rather than linking to an empty page.
 */
const getMenuItems = (role?: string): MenuItem[] => {
  const messaging: MenuItem[] = features.messaging
    ? [{ path: '/messages', label: 'Mensajes', icon: ICONS.chat }]
    : [];

  switch (role) {
    case 'TEACHER':
      return [
        { path: '/teacher/dashboard', label: 'Mi panel', icon: ICONS.home },
        { path: '/teacher/courses', label: 'Mis cursos', icon: ICONS.grid },
        { path: '/teacher/courses/new', label: 'Crear curso', icon: ICONS.plus },
        { path: '/teacher/calendar', label: 'Mi calendario', icon: ICONS.calendar },
        { path: '/teacher/availability', label: 'Mi disponibilidad', icon: ICONS.calendar },
        { path: '/teacher/earnings', label: 'Mis ingresos', icon: ICONS.money },
        { path: '/teacher/settlement', label: 'Mi liquidación', icon: ICONS.money },
        { path: '/courses', label: 'Catálogo', icon: ICONS.catalog },
        ...messaging,
        ...PROFILE_ITEMS,
      ];

    case 'PARENT':
      return [
        { path: '/parent/dashboard', label: 'Mi panel', icon: ICONS.home },
        { path: '/courses', label: 'Cursos', icon: ICONS.catalog },
        { path: '/parent/students/new', label: 'Agregar alumno', icon: ICONS.plus },
        { path: '/parent/kid-mode', label: 'Modo niño', icon: ICONS.user },
        ...messaging,
        ...PROFILE_ITEMS,
      ];

    case 'ADMIN':
    case 'MODERATOR':
      return [
        { path: '/admin/dashboard/ops', label: 'Ops', icon: ICONS.home },
        { path: '/admin/dashboard/go-no-go', label: 'Go/No-Go', icon: ICONS.grid },
        { path: '/admin/teacher-applications', label: 'Solicitudes', icon: ICONS.user },
        { path: '/admin/course-reviews', label: 'Revisión de cursos', icon: ICONS.grid },
        { path: '/admin/disputes', label: 'Disputas', icon: ICONS.money },
        { path: '/admin/support/queue', label: 'Soporte', icon: ICONS.chat },
        ...(role === 'ADMIN'
          ? [{ path: '/admin/payout-status', label: 'Pagos / liquidación', icon: ICONS.money }]
          : []),
        { path: '/courses', label: 'Catálogo', icon: ICONS.catalog },
        ...PROFILE_ITEMS,
      ];

    default:
      return [
        { path: '/courses', label: 'Cursos', icon: ICONS.catalog },
        ...messaging,
        ...PROFILE_ITEMS,
      ];
  }
};

// ── App ───────────────────────────────────────────────────────────────────────

const App: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.auth.user);
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const isInitializing = useSelector((state: RootState) => state.auth.isInitializing);

  // Restore session on page load.
  // Tokens are persisted in localStorage under 'iqmaxx:*' keys after login.
  // We read them back here so the Redux store stays in sync after a hard
  // refresh without forcing the user to log in again.
  useEffect(() => {
    if (isAuthenticated) return;

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

    async function restoreSession() {
      const stored = getStoredTokens();

      if (!stored.idToken) {
        dispatch(setInitialized());
        return;
      }

      let currentIdToken: string = stored.idToken;

      // If idToken is expired try to refresh silently
      if (isTokenExpired(currentIdToken)) {
        if (!stored.refreshToken) {
          clearTokens();
          dispatch(setInitialized());
          return;
        }
        try {
          const res = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken: stored.refreshToken }),
          });
          if (!res.ok) throw new Error('refresh failed');
          const data = await res.json();
          saveTokens({
            idToken: data.idToken,
            accessToken: data.accessToken,
            refreshToken: stored.refreshToken,
            expiresIn: data.expiresIn,
          });
          currentIdToken = data.idToken;
        } catch {
          clearTokens();
          dispatch(setInitialized());
          return;
        }
      }

      // Decode user claims from the idToken (no network call needed)
      const claims = decodeIdToken(currentIdToken);
      if (!claims) {
        clearTokens();
        dispatch(setInitialized());
        return;
      }

      dispatch(
        login({
          user: {
            id: claims.sub,
            email: claims.email,
            role: claims['custom:role'] ?? '',
            firstName: claims.given_name ?? '',
            lastName: claims.family_name ?? '',
          },
          accessToken: currentIdToken,
        }),
      );
    }

    restoreSession();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // PRT-17-FR-01 / PRT-BR-P01-08 — a session the server has stopped accepting
  // signs the UI out. The API client clears the tokens and announces it; this
  // is where the announcement becomes Redux state. ProtectedRoute then sends
  // the parent to sign in, and public pages simply lose the signed-in chrome.
  useEffect(() => onAuthExpired(() => dispatch(logout())), [dispatch]);

  // PRT-08-FR-07 / PRT-BR-P01-04 — the learner context comes from the server
  // every time a parent's session is (re)established, never from what this
  // browser remembered. Keyed on the user id so a different parent signing in
  // on the same tab gets their own context, not the previous one's.
  const userId = user?.id;
  const isParent = user?.role === 'PARENT';
  useEffect(() => {
    if (isAuthenticated && isParent) dispatch(restoreContext());
  }, [dispatch, isAuthenticated, isParent, userId]);

  // PRT-17-FR-01 — revokes server-side, clears tokens and the learner context,
  // lands on /login. The same routine every console's sign-out uses, so this
  // menu and theirs cannot drift apart again.
  const handleLogout = useSignOut();

  // The catalog is public (PRT-BR-P01-01/02): a visitor browses and opens a
  // course without an account, which is what the landing page's CTAs promise.
  // It used to sit inside the protected tree, so every "explore" link on the
  // home page ended at /login — prototype defect PG-01, reproduced. A
  // signed-in user keeps the app chrome; only the shell differs. Enrolling
  // stays protected, and the guard brings the parent back to it after sign-in.
  const catalogShell = (screen: React.ReactElement) => {
    // Wait for session restore so a signed-in parent is not shown the public
    // shell for a frame and then remounted into the app one.
    if (isInitializing) return null;
    return isAuthenticated ? (
      <KidModeGate>
        <MainLayout
          menuItems={getMenuItems(user?.role)}
          userEmail={user?.email}
          onLogout={handleLogout}
        >
          {screen}
        </MainLayout>
      </KidModeGate>
    ) : (
      <PublicLayout>{screen}</PublicLayout>
    );
  };

  return (
    <Routes>
      {/* Public catalog — see catalogShell. */}
      <Route path="/courses" element={catalogShell(<CourseSearch />)} />
      <Route path="/courses/:courseId" element={catalogShell(<CourseDetail />)} />
      {/* The teacher profile is reached from a public course page; it was
          left in the protected tree when the catalog moved out, so a
          signed-out parent clicking the teacher was sent to sign in. */}
      <Route path="/teachers/:teacherId" element={catalogShell(<TeacherProfile />)} />

      {/* Auth routes — public layout */}
      <Route
        path="/auth"
        element={
          <PublicLayout>
            <AuthChoicePage />
          </PublicLayout>
        }
      />
      <Route
        path="/login"
        element={
          <PublicLayout>
            <Login />
          </PublicLayout>
        }
      />
      <Route
        path="/register"
        element={
          <PublicLayout>
            <Register />
          </PublicLayout>
        }
      />
      {/* Account recovery + email confirmation. These must live in the PUBLIC
          block: the user is by definition not authenticated yet, and routing
          them through the protected tree bounces them back to /login. */}
      <Route
        path="/verify-email"
        element={
          <PublicLayout>
            <VerifyEmail />
          </PublicLayout>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicLayout>
            <ForgotPassword />
          </PublicLayout>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicLayout>
            <ResetPassword />
          </PublicLayout>
        }
      />

      {/* Teacher acquisition landing (ADR-007 S2.1 / TCH-01). Public and shown
          to signed-in users too: a teacher mid-application follows this link
          from the footer to re-read the terms of teaching, and the screen sends
          them on to their application rather than to signup. */}
      <Route
        path="/teach"
        element={
          <PublicLayout>
            <TeacherLanding />
          </PublicLayout>
        }
      />

      {/* Static pages — public, and reachable whether or not you are signed in.
          /terms and /privacy in particular MUST resolve: registration asks the
          user to accept both, and collecting consent against a 404 is not
          consent (ADR-007 S1.2, guarded by route-integrity.test.ts). */}
      <Route
        path="/terms"
        element={
          <PublicLayout>
            <Terms />
          </PublicLayout>
        }
      />
      <Route
        path="/privacy"
        element={
          <PublicLayout>
            <Privacy />
          </PublicLayout>
        }
      />
      <Route
        path="/help"
        element={
          <PublicLayout>
            <Help />
          </PublicLayout>
        }
      />
      <Route
        path="/contact"
        element={
          <PublicLayout>
            <Contact />
          </PublicLayout>
        }
      />

      {/* Homepage — public layout. Authenticated users are sent to the catalog
          instead of the marketing page with its login/register CTAs. */}
      <Route
        path="/"
        element={
          isAuthenticated ? (
            // Role-aware landing. Previously every signed-in user was sent to the
            // public catalog, so a teacher clicking "Inicio" landed on a page of
            // other teachers' courses offering to enrol them.
            <KidModeGate>
              <Navigate to={homePathForRole(user?.role)} replace />
            </KidModeGate>
          ) : (
            <PublicLayout>
              <HomePage />
            </PublicLayout>
          )
        }
      />

      {/* Educator portal — the redesign shell (docs/redesign/IQMaxx).
          Declared before the `/*` catch-all so it wins the match, and outside
          MainLayout because it brings its own chrome: full-viewport header,
          rail and content column, scoped to `.env-teacher`. */}
      <Route
        path="/teacher"
        element={
          <ProtectedRoute roles={['TEACHER']}>
            <PortalShell environment={TEACHER_ENVIRONMENT} />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/teacher/dashboard" replace />} />
        <Route path="/teacher/dashboard" element={<TeacherDashboard />} />
        <Route path="/teacher/courses" element={<TeacherCourses />} />
        <Route path="/teacher/courses/new" element={<TeacherCourseForm />} />
        <Route path="/teacher/courses/:courseId" element={<TeacherCourseDetail />} />
        <Route path="/teacher/courses/:courseId/edit" element={<TeacherCourseForm />} />
        <Route path="/teacher/calendar" element={<TeacherCalendar />} />
        <Route path="/teacher/availability" element={<TeacherAvailability />} />
        <Route path="/teacher/earnings" element={<TeacherEarnings />} />
        <Route path="/teacher/settlement" element={<TeacherSettlement />} />
        <Route path="/teacher/reviews" element={<TeacherReviews />} />
        {/* ADM-03-FR-06: evidence the integrity team asked this teacher for. */}
        <Route path="/teacher/evidence-requests" element={<TeacherEvidenceRequests />} />
        {/* Designed, not yet built — the prototype's own markup, clearly
            labelled, so the walkthrough has no dead links. */}
        <Route
          path="/teacher/students"
          element={<PrototypeScreen environment="teacher" screen="students" title="Estudiantes" />}
        />
        <Route
          path="/teacher/messages"
          element={<PrototypeScreen environment="teacher" screen="messages" title="Mensajes" />}
        />
        <Route
          path="/teacher/live"
          element={<PrototypeScreen environment="teacher" screen="live" title="Sesión en vivo" />}
        />
        <Route
          path="/teacher/stats"
          element={<PrototypeScreen environment="teacher" screen="stats" title="Estadísticas" />}
        />
        <Route
          path="/teacher/sessions"
          element={<PrototypeScreen environment="teacher" screen="resched" title="Sesiones" />}
        />
      </Route>

      {/* Family console — the redesign's other chrome (docs/redesign/IQMaxx).
          `/courses` stays public and outside this shell; the parent's own
          catalog lives at /parent/explore so browsing does not leave the
          console mid-journey. */}
      <Route
        path="/parent"
        element={
          <ProtectedRoute roles={['PARENT']}>
            <KidModeGate>
              <ConsoleShell environment={FAMILY_ENVIRONMENT} />
            </KidModeGate>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/parent/dashboard" replace />} />
        <Route path="/parent/dashboard" element={<ParentDashboard />} />
        <Route path="/parent/kid-mode" element={<KidModeSetup />} />
        <Route path="/parent/students/new" element={<AddStudent />} />
        <Route path="/parent/explore" element={<CourseSearch />} />
        {/* UC-P10: learning history and family playback. */}
        <Route path="/parent/past-classes" element={<PastClasses />} />
        {/* PRT-12-FR-01/02: "Comprados" is retired; an old link lands on the
            classes the family took, never on a dead end. */}
        <Route path="/parent/purchased" element={<Navigate to="/parent/past-classes" replace />} />
        <Route path="/parent/purchases" element={<Navigate to="/parent/past-classes" replace />} />
        {/* UC-P08: the household's active learning and its calendar. */}
        <Route path="/parent/learning" element={<ParentLearning />} />
        {/* UC-P09: the server's progress snapshot, per learner. */}
        <Route path="/parent/progress" element={<ParentProgress />} />
        <Route path="/parent/schedule" element={<ParentSchedule />} />
        <Route
          path="/parent/messages"
          element={<PrototypeScreen environment="family" screen="messages" title="Mensajes" />}
        />
      </Route>

      {/* Operations console — the same chrome as the family, steel instead of
          blue. Screens the prototype generates at runtime from invented
          figures get DesignedNotBuilt rather than a ported dashboard: an ops
          console with decorative counts is worse than one with none. */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute roles={['ADMIN', 'MODERATOR']}>
            <ConsoleShell environment={ADMIN_ENVIRONMENT} />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/admin/dashboard/ops" replace />} />
        <Route path="/admin/cases" element={<AdminCaseQueue />} />
        <Route path="/admin/dashboard/ops" element={<AdminOpsDashboard />} />
        <Route path="/admin/dashboard/health" element={<AdminProductHealthDashboard />} />
        <Route path="/admin/dashboard/earnings" element={<AdminEarningsDashboard />} />
        <Route path="/admin/dashboard/go-no-go" element={<AdminGoNoGoDashboard />} />
        <Route path="/admin/payout-status" element={<AdminPayoutStatus />} />
        <Route path="/admin/teacher-applications" element={<AdminTeacherApplications />} />
        {/* UC-A08 — the course review gate; ops cases link to a course here. */}
        <Route path="/admin/course-reviews" element={<AdminCourseReviews />} />
        <Route path="/admin/course-reviews/:courseId" element={<AdminCourseReviews />} />
        <Route path="/admin/disputes" element={<AdminDisputes />} />
        <Route path="/admin/support/queue" element={<AdminSupportQueue />} />
        <Route path="/admin/support/case/:enrollmentId" element={<AdminSupportCase />} />
        {/* UC-A09: the teacher's operational profile and the supply grid,
            both read from their owners' records. */}
        <Route path="/admin/chain" element={<AdminTrustChain />} />
        <Route path="/admin/chain/:teacherId" element={<AdminTeacherProfile />} />
        <Route path="/admin/coverage" element={<AdminCoverage />} />
        {/* UC-A03: evidence & progress integrity, holds, evidence requests and
            class health. Integrity cases in the queue link here. */}
        <Route path="/admin/integrity" element={<AdminIntegrity />} />
        {/* Named by the redesign, generated at runtime there, absent here. */}
        <Route
          path="/admin/families"
          element={
            <DesignedNotBuilt
              title="Families"
              purpose="Las familias de la plataforma, sus hijos y el estado de sus inscripciones."
            />
          }
        />
        {/* UC-S09 → T&S: the restricted safety-report inbox. The server admits
            only operators holding the Trust & Safety role. */}
        <Route path="/admin/safety" element={<AdminSafetyInbox />} />
        {/* MC-09: the governed Trust & Safety operation and the nine
            activation conditions the safety_reports flag waits on. */}
        <Route path="/admin/safety/operations" element={<AdminSafetyOperations />} />
        <Route path="/admin/audit" element={<AdminAuditLog />} />
        <Route path="/admin/playbook" element={<AdminPlaybooks />} />
        <Route
          path="/admin/cohorts"
          element={
            <DesignedNotBuilt
              title="Cohorts"
              purpose="Retención y progreso por cohorte de inscripción."
            />
          }
        />
        <Route
          path="/admin/access"
          element={
            <DesignedNotBuilt
              title="Roles & access"
              purpose="Quién puede hacer qué dentro de la consola de operaciones."
            />
          }
        />
        <Route path="/admin/params" element={<AdminParameters />} />
      </Route>

      {/* Child space — the kid mode of a parent's session (GAP-S01-01). There
          are no child accounts: KidShell admits a PARENT whose server-side
          context is in kid mode, and returns to the Parent only through re-auth.
          UC-S07 "Mis clases" and calendar, S08 the class door, S11 progress,
          S12 past classes. */}
      <Route
        path="/student"
        element={
          <ProtectedRoute roles={['PARENT']}>
            <KidShell environment={KID_ENVIRONMENT} />
          </ProtectedRoute>
        }
      >
        <Route index element={<MyClasses />} />
        <Route path="/student/calendar" element={<LearnerCalendar />} />
        <Route path="/student/join/:bookingId" element={<JoinClass />} />
        <Route path="/student/progress" element={<MyProgress />} />
        <Route path="/student/past" element={<LearnerPastClasses />} />
        <Route path="/student/courses/:courseId" element={<LearnerCourseOverview />} />
        {/* UC-S03..S06 — the catalog, a teacher, the request to the family, and
            the learner's own profile. Same supply as the Parent's; no price
            authority and no Saved. */}
        <Route path="/student/explore" element={<LearnerExplore />} />
        <Route path="/student/explore/:courseId" element={<LearnerExploreCourse />} />
        <Route path="/student/teachers/:teacherId" element={<LearnerTeacher />} />
        <Route path="/student/requests" element={<LearnerRequests />} />
        <Route path="/student/profile" element={<LearnerProfile />} />
        {/* MC-05 — the learner's in-app notices; the only channel a minor has. */}
        <Route path="/student/notices" element={<LearnerNotices />} />
        {/* Retired or never-built destinations land on "Mis clases" rather than
            a dead end: Purchased was removed by PRT-D06 (STU-09-FR-08, A7). */}
        <Route path="/student/purchased" element={<Navigate to="/student" replace />} />
        <Route path="/student/home" element={<Navigate to="/student" replace />} />
        <Route path="/student/classes" element={<Navigate to="/student" replace />} />
        <Route path="/student/*" element={<Navigate to="/student" replace />} />
      </Route>

      {/* Authenticated app routes — protected, full layout with sidebar */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <KidModeGate>
              <MainLayout
                menuItems={getMenuItems(user?.role)}
                userEmail={user?.email}
                onLogout={handleLogout}
              >
                <Routes>
                  <Route path="/consent" element={<ConsentAcceptance />} />
                  <Route path="/courses/:courseId/enroll" element={<EnrollmentConfirm />} />
                  <Route
                    path="/enrollments/checkout/success"
                    element={<EnrollmentCheckoutReturn mode="success" />}
                  />
                  <Route
                    path="/enrollments/checkout/cancel"
                    element={<EnrollmentCheckoutReturn mode="cancel" />}
                  />
                  <Route
                    path="/sessions/:bookingId"
                    element={
                      <ProtectedRoute roles={['TEACHER', 'PARENT']}>
                        <SessionRoom guardianEntry />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/enrollments/:enrollmentId" element={<EnrollmentDetail />} />
                  <Route
                    path="/enrollments/:enrollmentId/sessions/:sequence"
                    element={
                      <ProtectedRoute roles={['TEACHER', 'PARENT']}>
                        <SessionEvidence />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/teacher-onboarding"
                    element={
                      <ProtectedRoute roles={['TEACHER']}>
                        <TeacherOnboarding />
                      </ProtectedRoute>
                    }
                  />
                  {/* No /messages route: messaging is deferred (S6.1) and has no
                    backend. The nav item is gated by features.messaging, so the
                    link and the screen will appear together when S6.1 lands —
                    rather than the nav advertising a page that does not exist. */}
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route
                    path="*"
                    element={
                      <div>
                        <h1>404 - Página No Encontrada</h1>
                      </div>
                    }
                  />
                </Routes>
              </MainLayout>
            </KidModeGate>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

export default App;
